/**
 * 2-Level Permission System
 *
 * Level 1: Role-based permissions (user's role determines base access)
 * Level 2: Folder security — Group Security v3 ADDITIVE-OR resolver (spec §5). Access is the UNION
 *          of matched grants across securities[{userId,isEdit}] / securityFolderTeams[{teamId,isEdit}]
 *          (teamId '-2' = Everyone-in-account) / securityGroupIds[{groupId,isEdit}]:
 *          canView = ANY layer matches, canEdit = OR of the matched entries' isEdit. A folder with no
 *          grants at all is OPEN (default-visible to its owning team). This mirrors the backend
 *          `resolveAccess`/`isOpenFolder` (open-api `shared/access-resolver.ts`) — the single source
 *          of truth — so the FE never shows an action the backend would reject.
 *
 * See: docs/subsystems/ted/fundamentals/folder-security.md
 */
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useRootActionLock } from '@/contexts/RootActionLockContext';
import {
  ROLE_TYPES,
  FOLDER_SECURITY_TYPES,
  type RoleType,
  type MeResponse,
  type BaseFolderSecurity,
  type CompanyInfo,
} from '@scio3/shared-types';

// Root-level admin roles (for isRootAdmin — rootRole is never TEAM_OWNER)
const ROOT_ADMIN_ROLES: RoleType[] = [ROLE_TYPES.ADMIN, ROLE_TYPES.SUPER_ADMIN, ROLE_TYPES.OWNER];

// Team-level admin roles (includes TEAM_OWNER for isTeamAdmin/isAdminOnTeam)
const ADMIN_ROLES: RoleType[] = [
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.OWNER,
  ROLE_TYPES.TEAM_OWNER,
];

// Standard roles that can have security fully disabled
const STANDARD_ROLES: RoleType[] = [
  ROLE_TYPES.OWNER,
  ROLE_TYPES.SUPER_ADMIN,
  ROLE_TYPES.ADMIN,
  ROLE_TYPES.USER,
];

type Entity = 'screen' | 'asset' | 'playlist' | 'schedule';

interface FolderContext {
  folder?: BaseFolderSecurity | null;
  /** For items inside folders, we need the parent folder's security settings */
  parentFolder?: BaseFolderSecurity | null;
}

interface FolderAccessResult {
  canView: boolean;
  canEdit: boolean;
}

/**
 * A folder is OPEN (default-visible — any same-team member gets view AND edit) ONLY when it carries
 * NO grants at all: securities, securityFolderTeams AND securityGroupIds are all empty. If ANY of the
 * three grant arrays is non-empty the folder is RESTRICTED → deny-by-default (access flows only
 * through a matching grant). Mirror of the backend `isOpenFolder` (open-api `access-resolver.ts`);
 * restricting via securityFolderTeams / securityGroupIds alone MUST NOT read as open.
 */
function isOpenFolder(folder: BaseFolderSecurity): boolean {
  return (
    !(folder.securities && folder.securities.length) &&
    !(folder.securityFolderTeams && folder.securityFolderTeams.length) &&
    !(folder.securityGroupIds && folder.securityGroupIds.length)
  );
}

/**
 * The additive-OR evaluator (spec §5) — mirror of the backend `resolveAccess`. Unions the matched
 * grants across the three layers: securities (userId), securityFolderTeams (teamId, incl the
 * Everyone-in-account sentinel '-2'), securityGroupIds (groupId). canView = any layer matches;
 * canEdit = OR of the matched entries' isEdit. Only-Admin ('-1' in securities) is a write-time
 * restrict — it never equals a real userId, so it simply fails to grant here (non-admins denied).
 */
export function resolveFolderAccess(
  folder: BaseFolderSecurity,
  userId: string | undefined,
  teamIds: string[],
  groupIds: string[],
): FolderAccessResult {
  const teamSet = new Set<string>([...teamIds, FOLDER_SECURITY_TYPES.EVERYONE_ACCOUNT]);
  const groupSet = new Set<string>(groupIds);
  // EVERYONE_TEAM ('0') in securities = "everyone on the folder's OWNING team" — it grants only when the
  // requester is a member of the folder's own team (raw teamIds, NOT the '-2' account sentinel). Mirrors
  // the backend access-resolver; without this the "Default Team — Everyone on this team" Editor grant is
  // missed and the folder reads as read-only for its own team members.
  const ownsFolderTeam = folder.teamId != null && teamIds.includes(folder.teamId);
  let canView = false;
  let canEdit = false;
  const grant = (isEdit?: boolean) => {
    canView = true;
    if (isEdit) canEdit = true;
  };
  for (const s of folder.securities ?? []) {
    if (
      s.userId === userId ||
      (s.userId === FOLDER_SECURITY_TYPES.EVERYONE_TEAM && ownsFolderTeam)
    ) {
      grant(s.isEdit);
    }
  }
  for (const t of folder.securityFolderTeams ?? []) {
    if (t.teamId != null && teamSet.has(t.teamId)) grant(t.isEdit);
  }
  for (const g of folder.securityGroupIds ?? []) {
    if (g.groupId != null && groupSet.has(g.groupId)) grant(g.isEdit);
  }
  return { canView, canEdit };
}

// -----------------------------------------------------------------------------
// Level 1: Role-based permissions
// -----------------------------------------------------------------------------

/**
 * Level 1: Role-based permissions (pure function, testable without React).
 * Evaluates RBAC rules based on user's root role and active team role.
 */
export function getRolePermissions(me: MeResponse | null, activeTeamId: string) {
  const currentTeam = me?.teams.find((t) => t._id === activeTeamId);
  const rootRole = me?.rootRole;
  const teamRole = currentTeam?.role;

  const isRootOwner = rootRole === ROLE_TYPES.OWNER;
  const isRootAdmin = ROOT_ADMIN_ROLES.includes(rootRole as RoleType);
  const isSuperAdmin = rootRole === ROLE_TYPES.SUPER_ADMIN;
  const isRootSuperAdmin = isRootOwner || isSuperAdmin;
  const isTeamAdmin = ADMIN_ROLES.includes(teamRole as RoleType);
  // Billing (D4): the Billing role reaches Billing & Orders (payment + ecommerce, gated by
  // @RequireRole(BILLING_ROLES) on the backend) but is content read-only. So the Billing & Orders
  // nav entries show for root admins OR the Billing role — NOT for other below-admin roles.
  const isBillingRole = rootRole === ROLE_TYPES.BILLING;
  const canAccessBilling = isRootAdmin || isBillingRole;

  function canCreate(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // Custom role (ID > 1 char) without permissions = deleted role, deny all actions
    if (!perms && teamRole.length > 1) return false;
    if (perms) {
      return perms[`${entity}CanCreate`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return entity === 'asset';
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      // Billing (D4) is content READ-ONLY (its reach is Billing & Orders, not content) — same as
      // Read Only here. The backend enforces this too (ROLE_VALUE_TYPES[BILLING] = all content false).
      case ROLE_TYPES.READ_ONLY:
      case ROLE_TYPES.BILLING:
        return false;
      default:
        return true;
    }
  }

  function canEdit(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // Custom role (ID > 1 char) without permissions = deleted role, deny all actions
    if (!perms && teamRole.length > 1) return false;
    if (perms) {
      return perms[`${entity}CanEdit`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return false;
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      // Billing (D4) is content READ-ONLY — same as Read Only. Backend: ROLE_VALUE_TYPES[BILLING].
      case ROLE_TYPES.READ_ONLY:
      case ROLE_TYPES.BILLING:
        return false;
      default:
        return true;
    }
  }

  function canDelete(entity: Entity): boolean {
    // Auth not loaded yet — deny until we know the user's role
    if (!teamRole) return false;
    if (isTeamAdmin) return true;

    const perms = currentTeam?.permissions;
    // Custom role (ID > 1 char) without permissions = deleted role, deny all actions
    if (!perms && teamRole.length > 1) return false;
    if (perms) {
      return perms[`${entity}CanDelete`] ?? false;
    }

    switch (teamRole) {
      case ROLE_TYPES.CONTENT_PROPOSAL:
        return false;
      case ROLE_TYPES.CONTENT_APPROVAL:
        return true;
      // Billing (D4) is content READ-ONLY — same as Read Only. Backend: ROLE_VALUE_TYPES[BILLING].
      case ROLE_TYPES.READ_ONLY:
      case ROLE_TYPES.BILLING:
        return false;
      default:
        return true;
    }
  }

  return {
    rootRole,
    teamRole,
    isRootOwner,
    isRootAdmin,
    isSuperAdmin,
    isRootSuperAdmin,
    isTeamAdmin,
    isBillingRole,
    canAccessBilling,
    canCreate,
    canEdit,
    canDelete,
    currentTeam,
  };
}

// -----------------------------------------------------------------------------
// Level 2: Folder-level security
// -----------------------------------------------------------------------------

/**
 * Level 1 + Level 2: Combined permission check (pure function, testable without React).
 * Combines role-based permissions with folder-level security checks.
 */
export function getPermissions(
  me: MeResponse | null,
  company: CompanyInfo | null,
  activeTeamId: string,
  context: FolderContext = {},
) {
  const basePermissions = getRolePermissions(me, activeTeamId);
  const { teamRole } = basePermissions;

  const folder = context.folder ?? context.parentFolder;
  const userId = me?._id;

  // Feature flags from company
  const roleSecurity = company?.feature?.roleSecurity ?? true;
  // Folder-security master switch (v3 — the `userGroupSecurity` mode is GONE, D2). When on, folder
  // security is the base and group grants are layered additively (spec §5); no mode derivation.
  const folderLevelSecurity = company?.feature?.folderLevelSecurity ?? false;

  // Check if user is root super admin (bypass all)
  const isRootSuperAdmin = basePermissions.isRootSuperAdmin;

  // Check if user is admin on current team (bypass folder checks on their team)
  const isAdminOnTeam = ADMIN_ROLES.includes(teamRole as RoleType);

  // Check if this is definitively a SAME-team folder.
  // Only true when we KNOW the folder's teamId matches activeTeamId.
  // If teamId is undefined/null (e.g., cache miss or race condition), we treat it
  // as NOT same-team to be conservative and avoid granting unintended access.
  const isSameTeamFolder = folder?.teamId != null && folder.teamId === activeTeamId;

  // Legacy: kept for backwards compatibility in other parts of the return value
  const isCrossTeamFolder = folder?.teamId != null && folder.teamId !== activeTeamId;

  // Check if user has a standard role (can have security disabled)
  const hasStandardRole = STANDARD_ROLES.includes(teamRole as RoleType);

  // Check if roleSecurity is effectively enabled for this user
  const effectiveRoleSecurity = roleSecurity || !hasStandardRole;

  // Check if folderLevelSecurity is effectively enabled for this user
  const effectiveFolderSecurity = folderLevelSecurity || !hasStandardRole;

  // The principal used by the additive-OR resolver. Team reach is the CURRENT team only (matches the
  // backend single-folder gate — `teamIds: [teamForReach]`); the Everyone-in-account sentinel is added
  // inside resolveFolderAccess. Groups are the user's effective security-group ids.
  const principalTeamIds = [activeTeamId];
  const principalGroupIds = me?.groupIds ?? [];

  /**
   * View + edit capability on ONE folder, applying the same-team OPEN baseline. A folder with no
   * grants (open) is default-visible/editable to its OWNING team only; otherwise access is the §5
   * additive-OR union of the folder's grants. Cross-team access flows solely through securityFolderTeams
   * (incl the Everyone-in-account sentinel), evaluated by resolveFolderAccess.
   */
  function folderCapability(f: BaseFolderSecurity): FolderAccessResult {
    const sameTeam = f.teamId != null && f.teamId === activeTeamId;
    if (sameTeam && isOpenFolder(f)) return { canView: true, canEdit: true };
    return resolveFolderAccess(f, userId, principalTeamIds, principalGroupIds);
  }

  /**
   * Whether the user may ACT (create/edit/delete) in the folder context. Group Security v3 (spec §5):
   * the boolean gate maps to canEdit — a viewer-only grant (isEdit:false) reads as no access here so
   * the edit/create/delete affordances hide. Returns true when folder security is bypassed.
   */
  function checkFolderAccess(): boolean {
    // Root Super Admin bypasses all folder checks
    if (isRootSuperAdmin) return true;

    // If no folder context, use role permissions only
    if (!folder) {
      return true;
    }

    // System folders (OnHold) — admins bypass folder checks, non-admins denied
    if (folder.isHoldDevice) return isAdminOnTeam || isRootSuperAdmin;

    // Admin on SAME team bypasses ALL folder security checks. Team admins are the highest authority
    // on their own team — if they can't edit, nobody can. Cross-team admins do NOT get this bypass.
    // Only bypass when we KNOW it's same-team; an undefined folder.teamId conservatively denies it.
    if (isAdminOnTeam && isSameTeamFolder) {
      return true;
    }

    // Master gates: a standard-role user is unrestricted when roleSecurity or folderLevelSecurity is
    // OFF (mirrors the backend — all folder logic, incl. group grants, is gated on folderLevelSecurity).
    if (!effectiveRoleSecurity) return true;
    if (!effectiveFolderSecurity) return true;

    // Group Security v3 — additive-OR (spec §5). Items inherit their folder by path (D3): an item op is
    // gated by context.parentFolder, a folder op by context.folder. The user must be able to VIEW the
    // primary folder, and hold canEdit on each folder present in the context (parent controls items
    // inside it; folder controls the folder itself — both must allow edit, immediate-children only).
    if (!folderCapability(folder).canView) return false;
    if (context.parentFolder && !folderCapability(context.parentFolder).canEdit) return false;
    if (context.folder && !folderCapability(context.folder).canEdit) return false;
    return true;
  }

  const hasFolderAccess = checkFolderAccess();

  // Detect if folder context was provided
  const hasFolderContext = !!(context.folder || context.parentFolder);

  /**
   * Check folder-level permission for edit action.
   */
  function canEditInFolder(entity: Entity): boolean {
    return basePermissions.canEdit(entity) && hasFolderAccess;
  }

  /**
   * Check folder-level permission for delete action.
   */
  function canDeleteInFolder(entity: Entity): boolean {
    return basePermissions.canDelete(entity) && hasFolderAccess;
  }

  /**
   * Check folder-level permission for create action.
   */
  function canCreateInFolder(entity: Entity): boolean {
    return basePermissions.canCreate(entity) && hasFolderAccess;
  }

  // Auto-select folder-aware when context exists, otherwise use role-based
  const canCreate = (entity: Entity) =>
    hasFolderContext ? canCreateInFolder(entity) : basePermissions.canCreate(entity);
  const canEdit = (entity: Entity) =>
    hasFolderContext ? canEditInFolder(entity) : basePermissions.canEdit(entity);
  const canDelete = (entity: Entity) =>
    hasFolderContext ? canDeleteInFolder(entity) : basePermissions.canDelete(entity);

  return {
    ...basePermissions,
    canCreate,
    canEdit,
    canDelete,
    hasFolderAccess,
    hasFolderContext,
    folderLevelSecurity,
    roleSecurity,
    effectiveFolderSecurity,
    effectiveRoleSecurity,
    isCrossTeamFolder: !!isCrossTeamFolder,
  };
}

// -----------------------------------------------------------------------------
// React Hooks
// -----------------------------------------------------------------------------

/**
 * React hook for combined permissions (Level 1 + Level 2).
 *
 * When folder context is provided, canEdit/canCreate/canDelete auto-select folder-aware checks.
 * When no folder context, they use role-based checks only.
 *
 * @example
 * // Without folder context - uses role-based permissions
 * const { canEdit } = usePermissions();
 * canEdit('screen'); // Role-based only
 *
 * @example
 * // With folder context - auto-selects folder-aware permissions
 * const { canEdit } = usePermissions({ parentFolder });
 * canEdit('screen'); // Role + folder security combined
 *
 * @param context - Optional folder context for folder-level security checks
 */
export function usePermissions(
  context: FolderContext = {},
  options: { ignoreRootLock?: boolean } = {},
) {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const perms = getPermissions(me, company, activeTeamId, context);
  // §12 HOME PATH lock: inside a Root-locked content subtree (path=null, non-admin group member with
  // canEditHomePath=false), ITEM create/edit/delete is disabled to mirror the backend Root gate. This
  // applies ONLY to no-folder-context checks (loose items at Root). FOLDER operations are NOT gated —
  // they are governed by folder/group security (securityGroupIds): callers that check FOLDER permissions
  // pass a folder context OR set `ignoreRootLock` (the folder-action hooks do the latter for their
  // role-only SHOW/HIDE check). Default false (no provider) ⇒ no effect anywhere else.
  const rootLocked = useRootActionLock();
  if (!rootLocked || options.ignoreRootLock || perms.hasFolderContext) return perms;
  return {
    ...perms,
    canCreate: () => false,
    canEdit: () => false,
    canDelete: () => false,
  };
}

/**
 * @deprecated Use usePermissions({ folder }) or usePermissions({ parentFolder }) instead.
 * Alias for usePermissions with folder context.
 */
export function useFolderPermissions(context: FolderContext = {}) {
  return usePermissions(context);
}
